import { useMemo } from 'react';
import BigNumber from 'bignumber.js';
import { Address } from 'viem';

import { useGetStakeTokenBalanceQuery } from 'modules/api/actions/getStakeTokenBalance';
import { EXPLORER_LINK, LIQUID_STAKING_TOKEN_ADDRESS } from 'modules/api/const';
import { useGetStakeTokenUSDRatio } from 'modules/api/hooks/useGetStakeTokenUSDRatio';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { ZERO } from 'modules/common/const';
import { stakeTokenIcon } from 'modules/common/icons';
import { useGlobalTranslation } from 'modules/i18n';
import { useGetLiquidStakingTokenQuery } from 'modules/liquidStaking/actions/getLiquidStakingToken';

import { useGetStakeTokenRatioQuery } from '../actions/getStakeTokenRatio';

interface IStakeToken {
  name: 'stMAT';
  address: Address;
  explorerLink: string;
  ratio: BigNumber;
  balance: BigNumber;
  fee?: BigNumber;
  isBalanceLoading: boolean;
  mainBalance: BigNumber; // balance in main token
  usdBalance: BigNumber;
  icon: string;
}

export function useStakeToken(
  poolAddress: Address = LIQUID_STAKING_TOKEN_ADDRESS,
): IStakeToken {
  const { t, keys } = useGlobalTranslation();

  const { isConnected, connectionKey, cacheTime } = useConnection();

  const { data } = useGetLiquidStakingTokenQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );
  const { data: balance = ZERO, isLoading: isBalanceLoading } =
    useGetStakeTokenBalanceQuery(
      { connectionKey, poolAddress },
      {
        skip: !isConnected,
        selectFromResult: mapDataToUndefinedIfSkip,
        refetchOnMountOrArgChange: cacheTime,
      },
    );
  const { data: ratio = ZERO } = useGetStakeTokenRatioQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );
  const usdRatio = useGetStakeTokenUSDRatio(poolAddress);

  const mainBalance = useMemo(
    () => ratio.multipliedBy(balance),
    [balance, ratio],
  );
  const usdBalance = useMemo(
    () => (usdRatio ? usdRatio.multipliedBy(balance) : ZERO),
    [balance, usdRatio],
  );

  return {
    balance,
    mainBalance,
    usdBalance,
    isBalanceLoading,
    ratio,
    fee: data?.fee,
    name: t(keys.tokens.stakeToken) as IStakeToken['name'],
    address: poolAddress,
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    explorerLink: `${EXPLORER_LINK}/address/${poolAddress}`,
    icon: stakeTokenIcon,
  };
}
